import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';

import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import { getListAllAPI, getListAPI } from '../../services';
import {
  CATEGORIAS_BLOCO_MAPA_LOJA,
  CATEGORIAS_ENDERECO_EXPOSICAO,
  STATUS_ATIVO,
} from '../../utils/constants';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'black',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 10000,
    color: '#fff',
  },
  content: {
    display: 'flex',
    height: '100%',
    padding: 0,
    paddingBottom: theme.spacing(5),
    overflow: 'hidden',
  },
  sidebarDireita: {
    width: '260px',
    flexShrink: 0,
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  cabecalhoFixo: {
    flexShrink: 0,
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  listaScrollavel: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  dicaSidebar: {
    display: 'block',
    marginBottom: theme.spacing(1.5),
  },
  divisorSidebar: {
    marginBottom: 0,
  },
  categoriaChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '8px',
    padding: '6px 8px',
    marginBottom: theme.spacing(1),
    userSelect: 'none',
  },
  categoriaCor: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  // O wrapper não rola (overflow: hidden) e serve só de âncora pros controles
  // de zoom flutuantes, que assim ficam sempre visíveis no canto — se o zoom
  // estivesse ligado ao elemento que rola, os botões sumiriam de vista ao
  // arrastar o mapa.
  areaMapaWrapper: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
  },
  areaMapa: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    // "safe center" evita o problema clássico de flexbox onde um conteúdo
    // centralizado maior que o container fica com uma parte inacessível
    // mesmo rolando — sem isso, dar zoom além do tamanho da tela cortaria
    // pedaços do grid que a rolagem não alcançaria.
    alignItems: 'safe center',
    justifyContent: 'safe center',
  },
  controlesZoom: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    zIndex: 1,
  },
  labelZoom: {
    minWidth: '40px',
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
  },
  celula: {
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    userSelect: 'none',
  },
  celulaOcupada: {
    borderStyle: 'solid',
  },
  // Destaque de produto (busca) é um "anel" por cima da cor da categoria, não
  // uma cor de fundo própria — evita confundir com a categoria "Ilha", que
  // também é verde.
  celulaDestaqueProduto: {
    borderStyle: 'solid',
    borderWidth: '3px',
    borderColor: '#0d47a1',
    boxShadow: '0 0 0 3px rgba(13, 71, 161, 0.45)',
    zIndex: 1,
  },
  legendaCategoriaEndereco: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  legendaCategoriaEnderecoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendaCategoriaEnderecoContagem: {
    marginLeft: '20px',
  },
  legendaCategoriaEnderecoCor: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    flexShrink: 0,
  },
  legendaDestaqueCor: {
    backgroundColor: '#fff',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#0d47a1',
    boxShadow: '0 0 0 2px rgba(13, 71, 161, 0.45)',
  },
  celulaConteudo: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    overflow: 'hidden',
    padding: '2px',
    boxSizing: 'border-box',
  },
}));

function getChaveCelula(x, y) {
  return `${x}-${y}`;
}

// Desativado por enquanto a pedido — os blocos por categoria (paredes, área
// neutra, depósito) ficam escondidos da interface, mas o código continua
// intacto pra reativar rápido no futuro só virando isso pra "true" de novo.
const BLOCOS_CATEGORIA_HABILITADO = false;

const DialogoVisualizarMapaLoja = forwardRef(({ andarFiltro = null, titulo = '' }, ref) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [enderecosBrutos, setEnderecosBrutos] = useState([]);
  const [blocos, setBlocos] = useState([]);
  const [largura, setLargura] = useState(10);
  const [altura, setAltura] = useState(10);
  const [nomeMapa, setNomeMapa] = useState('');

  const [produtoFiltro, setProdutoFiltro] = useState(null);
  const [inputValueProduto, setInputValueProduto] = useState('');
  const [valueDebounceProduto, setValueDebounceProduto] = useState('');
  const [optionsProduto, setOptionsProduto] = useState([]);

  useEffect(() => {
    const timer = setTimeout(
      () => setValueDebounceProduto(inputValueProduto),
      500,
    );
    return () => clearTimeout(timer);
  }, [inputValueProduto]);

  useEffect(() => {
    if (!andarFiltro) return undefined;

    let active = true;
    (async () => {
      const { data } = await getListAPI(
        'produtos_search_filter',
        ['id', 'asc'],
        [1, 10],
        {
          nome: valueDebounceProduto,
          status: STATUS_ATIVO,
        },
        [],
      );
      if (active) setOptionsProduto(data);
    })();

    return () => {
      active = false;
    };
  }, [valueDebounceProduto, andarFiltro]);

  const areaMapaRef = useRef(null);
  const celulaDestaqueRef = useRef(null);
  const [tamanhoBase, setTamanhoBase] = useState(20);
  const [zoom, setZoom] = useState(1.75);

  const GAP_CELULA = 2;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 5;
  const ZOOM_PASSO = 0.25;

  const tamanhoCelula = tamanhoBase * zoom;

  function handleZoomIn() {
    setZoom((prev) => Math.min(ZOOM_MAX, Math.round((prev + ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomOut() {
    setZoom((prev) => Math.max(ZOOM_MIN, Math.round((prev - ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomReset() {
    setZoom(1);
  }

  useEffect(() => {
    const elemento = areaMapaRef.current;
    if (!elemento) return undefined;

    function recalcular() {
      const { clientWidth, clientHeight } = elemento;
      const larguraDisponivel = clientWidth - GAP_CELULA * (largura - 1);
      const alturaDisponivel = clientHeight - GAP_CELULA * (altura - 1);
      const tamanho = Math.floor(
        Math.min(larguraDisponivel / largura, alturaDisponivel / altura),
      );
      setTamanhoBase(Math.max(tamanho, 4));
    }

    recalcular();

    const observer = new ResizeObserver(recalcular);
    observer.observe(elemento);
    return () => observer.disconnect();
  }, [largura, altura, carregando]);

  // Quando o diálogo abre já com um produto pra destacar (ex: vindo da tela
  // "Produtos Expostos"), rola o mapa até a célula destacada — sem isso, num
  // mapa grande o usuário pode nem perceber que o destaque apareceu.
  useEffect(() => {
    if (carregando || !produtoFiltro || !celulaDestaqueRef.current) return;
    celulaDestaqueRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [carregando, produtoFiltro]);

  async function getDataResource(idMapa) {
    setCarregando(true);
    try {
      const dataMapa = await getListAllAPI(
        'mapas_loja',
        ['id', 'asc'],
        { id: [idMapa] },
        [],
      );
      if (dataMapa.data.length === 0) throw 'Este mapa não existe!';

      const dataEnderecos = await getListAllAPI(
        'enderecos_exposicao_mercadorias',
        ['codigo', 'asc'],
        { status: STATUS_ATIVO, mapa_loja_id: idMapa },
        ['produto', 'andaresProdutos'],
      );

      let dataBlocos = { data: [] };
      if (BLOCOS_CATEGORIA_HABILITADO) {
        dataBlocos = await getListAllAPI(
          'blocos_mapa_loja',
          ['id', 'asc'],
          { mapa_loja_id: idMapa },
          [],
        );
      }

      setLargura(dataMapa.data[0].largura);
      setAltura(dataMapa.data[0].altura);
      setNomeMapa(dataMapa.data[0].nome);
      setEnderecosBrutos(dataEnderecos.data);
      setBlocos(dataBlocos.data);
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(idMapa, produtoInicial = null) {
      setOpen(true);
      setZoom(1.75);
      setProdutoFiltro(produtoInicial);
      setInputValueProduto(produtoInicial ? produtoInicial.nome : '');
      getDataResource(idMapa);
    },
  }));

  function handleClose() {
    setOpen(false);
  }

  // Quando tem filtro de andar (1º/2º Aéreo), mostra todo endereço que TEM
  // aquele andar (quantidade_andares >= andarFiltro), tenha produto ativo
  // nele ou não — antes só aparecia se já tivesse produto, escondendo
  // endereços com aquele andar vazio. O filtro de produto NÃO esconde mais
  // endereços — ele só destaca (com outra cor) os que têm aquele produto.
  const enderecos = andarFiltro
    ? enderecosBrutos.filter((e) => (e.quantidade_andares || 1) >= andarFiltro)
    : enderecosBrutos;

  function correspondeProdutoFiltro(endereco) {
    if (!produtoFiltro) return false;
    return (endereco.andaresProdutos || []).some(
      (ap) =>
        ap.andar === andarFiltro &&
        ap.status === STATUS_ATIVO &&
        ap.produto_id === produtoFiltro.id,
    );
  }

  const mapaEnderecosPorCelula = {};
  enderecos.forEach((e) => {
    if (e.posicao_x !== null && e.posicao_x !== undefined && e.posicao_y !== null && e.posicao_y !== undefined) {
      mapaEnderecosPorCelula[getChaveCelula(e.posicao_x, e.posicao_y)] = e;
    }
  });

  const mapaBlocosPorCelula = {};
  blocos.forEach((b) => {
    mapaBlocosPorCelula[getChaveCelula(b.posicao_x, b.posicao_y)] = b;
  });

  const corPorCategoria = {};
  CATEGORIAS_BLOCO_MAPA_LOJA.forEach((c) => {
    corPorCategoria[c.categoria] = c.cor;
  });

  const corPorCategoriaEndereco = {};
  const corBordaPorCategoriaEndereco = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    corPorCategoriaEndereco[c.categoria] = c.cor;
    corBordaPorCategoriaEndereco[c.categoria] = c.corBorda;
  });

  function possuiProdutoAtivo(endereco) {
    if (andarFiltro === 1 && endereco.produto) return true;
    return (endereco.andaresProdutos || []).some(
      (ap) => ap.andar === andarFiltro && ap.status === STATUS_ATIVO,
    );
  }

  const contagemPorCategoria = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    // Só entram na contagem os endereços já posicionados neste mapa.
    const doCategoriaNoMapa = enderecos.filter(
      (e) =>
        e.categoria === c.categoria &&
        e.posicao_x !== null &&
        e.posicao_x !== undefined &&
        e.posicao_y !== null &&
        e.posicao_y !== undefined,
    );
    const preenchidos = doCategoriaNoMapa.filter(possuiProdutoAtivo).length;
    contagemPorCategoria[c.categoria] = {
      preenchidos,
      disponiveis: doCategoriaNoMapa.length - preenchidos,
    };
  });

  const celulas = [];
  for (let y = 0; y < altura; y += 1) {
    for (let x = 0; x < largura; x += 1) {
      celulas.push({
        x,
        y,
        endereco: mapaEnderecosPorCelula[getChaveCelula(x, y)] || null,
        bloco: mapaBlocosPorCelula[getChaveCelula(x, y)] || null,
      });
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="dialogo-visualizar-mapa-loja"
      open={open}
      fullScreen
    >
      <DialogTitle onClose={handleClose}>
        {titulo ? `${nomeMapa} - ${titulo}` : nomeMapa}
      </DialogTitle>
      <Divider />
      <MuiDialogContent className={classes.content}>
        {carregando ? (
          <Backdrop open className={classes.backdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <>
            <div className={classes.areaMapaWrapper} ref={areaMapaRef}>
              <div className={classes.areaMapa}>
                <div
                  className={classes.grid}
                  style={{
                    gridTemplateColumns: `repeat(${largura}, ${tamanhoCelula}px)`,
                    gridTemplateRows: `repeat(${altura}, ${tamanhoCelula}px)`,
                    gap: `${GAP_CELULA}px`,
                  }}
                >
                  {celulas.map(({ x, y, endereco, bloco }) => {
                    const destaque = endereco
                      ? correspondeProdutoFiltro(endereco)
                      : false;
                    return (
                    <div
                      key={getChaveCelula(x, y)}
                      ref={destaque ? celulaDestaqueRef : undefined}
                      className={`${classes.celula} ${
                        endereco ? classes.celulaOcupada : ''
                      } ${destaque ? classes.celulaDestaqueProduto : ''}`}
                      style={
                        endereco
                          ? {
                              backgroundColor: corPorCategoriaEndereco[endereco.categoria],
                              borderColor: destaque
                                ? undefined
                                : corBordaPorCategoriaEndereco[endereco.categoria],
                            }
                          : BLOCOS_CATEGORIA_HABILITADO && bloco
                          ? { backgroundColor: corPorCategoria[bloco.categoria] }
                          : undefined
                      }
                    >
                      {endereco ? (
                        <div className={classes.celulaConteudo}>
                          <Typography
                            style={{
                              fontWeight: 'bold',
                              fontSize: `${Math.max(tamanhoCelula * 0.3, 9)}px`,
                              lineHeight: 1.1,
                            }}
                          >
                            {endereco.codigo}
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              </div>
              <Paper className={classes.controlesZoom} elevation={3}>
                <IconButton size="small" onClick={handleZoomOut}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" className={classes.labelZoom}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton size="small" onClick={handleZoomIn}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleZoomReset}
                  title="Ajustar à tela"
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Paper>
            </div>
            {andarFiltro || BLOCOS_CATEGORIA_HABILITADO ? (
              <div className={classes.sidebarDireita}>
                <div className={classes.cabecalhoFixo}>
                  {andarFiltro ? (
                    <>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Filtrar por produto
                      </Typography>
                      <Autocomplete
                        value={produtoFiltro}
                        onChange={(event, newValue) => setProdutoFiltro(newValue)}
                        inputValue={inputValueProduto}
                        onInputChange={(event, newInputValue) =>
                          setInputValueProduto(newInputValue)
                        }
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.nome || ''}
                        options={optionsProduto}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            margin="dense"
                            label="Produto"
                            fullWidth
                          />
                        )}
                      />
                      <Divider className={classes.divisorSidebar} style={{ marginTop: 12 }} />
                      <div className={classes.legendaCategoriaEndereco}>
                        {CATEGORIAS_ENDERECO_EXPOSICAO.map((c) => (
                          <div key={c.categoria}>
                            <div className={classes.legendaCategoriaEnderecoItem}>
                              <div
                                className={classes.legendaCategoriaEnderecoCor}
                                style={{ backgroundColor: c.cor }}
                              />
                              <Typography variant="caption">{c.label}</Typography>
                            </div>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              className={classes.legendaCategoriaEnderecoContagem}
                            >
                              {`${contagemPorCategoria[c.categoria].preenchidos} preenchido${
                                contagemPorCategoria[c.categoria].preenchidos === 1 ? '' : 's'
                              } · ${contagemPorCategoria[c.categoria].disponiveis} disponível${
                                contagemPorCategoria[c.categoria].disponiveis === 1 ? '' : 'is'
                              }`}
                            </Typography>
                          </div>
                        ))}
                        <div className={classes.legendaCategoriaEnderecoItem}>
                          <div
                            className={`${classes.legendaCategoriaEnderecoCor} ${classes.legendaDestaqueCor}`}
                          />
                          <Typography variant="caption">Produto selecionado</Typography>
                        </div>
                      </div>
                      {BLOCOS_CATEGORIA_HABILITADO ? (
                        <Divider className={classes.divisorSidebar} style={{ marginTop: 12 }} />
                      ) : null}
                    </>
                  ) : null}
                  {BLOCOS_CATEGORIA_HABILITADO ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 'bold', marginTop: andarFiltro ? 12 : 0 }}
                      >
                        Categorias
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        className={classes.dicaSidebar}
                      >
                        Legenda das cores do mapa
                      </Typography>
                      <Divider className={classes.divisorSidebar} />
                    </>
                  ) : null}
                </div>
                {BLOCOS_CATEGORIA_HABILITADO ? (
                  <div className={classes.listaScrollavel}>
                    {CATEGORIAS_BLOCO_MAPA_LOJA.map((c) => (
                      <div key={c.categoria} className={classes.categoriaChip}>
                        <div
                          className={classes.categoriaCor}
                          style={{ backgroundColor: c.cor }}
                        />
                        <Typography variant="body2">{c.label}</Typography>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </MuiDialogContent>
    </Dialog>
  );
});

export default DialogoVisualizarMapaLoja;
